import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import Button from '@mui/material/Button';
import {
	Callout,
	Dialogue,
	FlexContainer,
	RuSecondaryButton,
	Spinner,
} from '@silverrailtech/srtlibrary';

import messages from './messages';
import {
	SpinnerWrapper,
	SuspendedCallout,
	SuspendedText,
	DialogTitle,
	SuccessIcon,
} from './STicketActionsStyled';

const BUTTON_CONFIG = {
	remove: {
		color: 'error',
		idPrefix: 'srtBookingRemoveDeviceId',
		title: messages.btnRemoveDeviceId,
		description: messages.dlgRemoveDeviceIdDescription,
		primaryLabel: messages.btnRemoveDeviceId,
		buttonLabel: messages.btnRemoveDeviceId,
	},
	suspend: {
		color: 'warning',
		idPrefix: 'srtBookingSuspendTicket',
		title: messages.btnSuspendTicket,
		description: messages.dlgSuspendTicketDescription,
		primaryLabel: messages.btnSuspendTicket,
		buttonLabel: messages.btnSuspendTicket,
	},
	reactivate: {
		color: 'success',
		idPrefix: 'srtBookingReactivateTicket',
		title: messages.dlgReactivateTicketTitle,
		description: messages.dlgReactivateTicketDescription,
		primaryLabel: messages.dlgReactivateTicketTitle,
		buttonLabel: messages.btnReactivateTicket,
	},
};

const STicketActions = ({
	orderId,
	isProcessingAction,
	sTicketStatus,
	handleSTicketAction,
	handleSTicketDialogClose,
	isSTKSuspended,
	isSTKCanSuspend,
	isSTKCanReactivate,
	isSTKCanResetDeviceId,
}) => {
	const [sTicketDialogConfig, setSTicketDialogConfig] = useState({
		open: false,
		title: {},
		description: {},
		primaryLabel: {},
		onConfirm: () => {},
		flowType: '',
	});

	const openConfirmDialog = (flowType) => {
		const config = BUTTON_CONFIG[flowType];
		setSTicketDialogConfig({
			open: true,
			title: config.title,
			description: config.description,
			primaryLabel: config.primaryLabel,
			onConfirm: () => handleSTicketAction(flowType),
			flowType,
		});
	};

	const closeDialog = () =>
		setSTicketDialogConfig({
			open: false,
			title: {},
			description: {},
			primaryLabel: {},
			onConfirm: () => {},
			flowType: '',
		});

	const cancelAction = {
		label: <FormattedMessage {...messages.btnCancel} />,
		onClick: closeDialog,
		color: 'neutral_1',
	};

	const confirmAction = {
		label: <FormattedMessage {...sTicketDialogConfig.primaryLabel} />,
		onClick: () => sTicketDialogConfig.onConfirm(),
		color:
			sTicketDialogConfig.flowType === 'reactivate' ? 'positive' : 'negative',
	};

	const getDialogPropsByStatus = (status) => {
		switch (status) {
			case 'loading':
				return {
					description: (
						<SpinnerWrapper padding="0">
							<Spinner width="64px" height="64px" className="spinner-padding" />
						</SpinnerWrapper>
					),
				};
			case 'success':
				return {
					description: (
						<FlexContainer
							padding="0"
							direction="column"
							align="center"
							justify="center"
							gap="0"
						>
							<SuccessIcon />
							<FormattedMessage {...messages.lblSuccess} />
						</FlexContainer>
					),
					primaryAction: {
						...cancelAction,
						onClick: () => {
							closeDialog();
							handleSTicketDialogClose();
						},
					},
				};
			case 'error':
				return {
					description: (
						<>
							<Callout variant="important" style={{ marginBottom: '16px' }}>
								<FormattedMessage {...messages.dlgSTicketError} />
							</Callout>
							<FormattedMessage {...sTicketDialogConfig.description} />
						</>
					),
					primaryAction: cancelAction,
					secondaryAction: confirmAction,
				};
			default:
				return {
					description: (
						<FormattedMessage {...sTicketDialogConfig.description} />
					),
					primaryAction: cancelAction,
					secondaryAction: confirmAction,
				};
		}
	};

	const visibleButtons = [
		{ flowType: 'remove', visible: isSTKCanResetDeviceId },
		{ flowType: 'suspend', visible: isSTKCanSuspend },
		{ flowType: 'reactivate', visible: isSTKCanReactivate },
	].filter((btn) => btn.visible);

	return (
		<>
			{isSTKSuspended && (
				<SuspendedCallout variant="important">
					<SuspendedText>
						<FormattedMessage {...messages.calloutSuspendedLabel} />
					</SuspendedText>{' '}
					<FormattedMessage {...messages.calloutSuspended} />
				</SuspendedCallout>
			)}
			{visibleButtons.length > 0 && (
				<FlexContainer direction="row" margin="16px 0 0 0">
					{visibleButtons.map(({ flowType }) => {
						const config = BUTTON_CONFIG[flowType];
						return (
							<Button
								key={flowType}
								variant="contained"
								color={config.color}
								id={`${config.idPrefix}_${orderId}`}
								data-testid={`sTicketAction-${flowType}`}
								style={{ width: '200px' }}
								disabled={isProcessingAction}
								onClick={() => openConfirmDialog(flowType)}
							>
								<FormattedMessage {...config.buttonLabel} />
							</Button>
						);
					})}
				</FlexContainer>
			)}
			{sTicketDialogConfig.open && (
				<div data-testid={`sTicketDialog-${sTicketDialogConfig.flowType}`}>
					<Dialogue
						open={sTicketDialogConfig.open}
						TitleComponent={DialogTitle}
						ButtonComponent={RuSecondaryButton}
						title={<FormattedMessage {...sTicketDialogConfig.title} />}
						{...getDialogPropsByStatus(sTicketStatus)}
					/>
				</div>
			)}
		</>
	);
};

STicketActions.propTypes = {
	orderId: PropTypes.string.isRequired,
	isProcessingAction: PropTypes.bool.isRequired,
	sTicketStatus: PropTypes.string.isRequired,
	handleSTicketAction: PropTypes.func.isRequired,
	handleSTicketDialogClose: PropTypes.func.isRequired,
	isSTKSuspended: PropTypes.bool,
	isSTKCanSuspend: PropTypes.bool,
	isSTKCanReactivate: PropTypes.bool,
	isSTKCanResetDeviceId: PropTypes.bool,
};

export default STicketActions;
